import { ComputedRef, ref, computed, Ref, onMounted } from 'vue';
import type { Badge } from '@ankorstore/design-system';
import { getAccountMenu } from '../services/account-menu';
import { useRoute, useRouter } from 'vue-router';

interface RawMenuItemChild {
  id: string;
  label: string;
  route: string;
  highlight?: MenuItemHighlight;
  metadata?: {
    sideBadge?: Badge;
  };
}

export interface RawMenuItem {
  id: string;
  label: string;
  icon?: string;
  position: 'top' | 'bottom';
  route: string;
  badge?: number;
  external?: boolean;
  children?: RawMenuItemChild[];
  metadata?: {
    section?: string;
    iconKind?: string;
    sideBadge?: Badge;
    tooltipKey?: string;
    hideOnMobile?: boolean;
    hideOnDesktop?: boolean;
    matchRoutes?: string[];
    surfaces?: string[];
  };
}

export interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  position: 'top' | 'bottom';
  route: string;
  badge?: number;
  external?: boolean;
  section?: string;
  iconKind?: string;
  sideBadge?: Badge;
  tooltipKey?: string;
  hideOnMobile?: boolean;
  hideOnDesktop?: boolean;
  matchRoutes?: string[];
  surfaces?: string[];
  children?: MenuItemChild[];
}

interface MenuItemHighlight {
  tag?: string;
  title: string;
  description: string;
  ctaLabel?: string;
  ctaRoute?: string;
  dismissKey?: string;
  releaseDate?: string;
}

interface MenuItemChild {
  id: string;
  label: string;
  route: string;
  highlight?: MenuItemHighlight;
  sideBadge?: Badge;
}

export interface UseAccountMenuReturn {
  items: Ref<MenuItem[]>;
  navigate: (route: string) => void;
  getCurrentUrl: ComputedRef<string>;
}

export const flattenMetadata = (raw: RawMenuItem): MenuItem => {
  const { metadata, children, ...rest } = raw;
  return {
    ...metadata,
    ...rest,
    children: children?.map(({ metadata: childMeta, ...childRest }) => ({
      ...childMeta,
      ...childRest,
    })),
  };
};

// Shared cache to avoid duplicate API calls across multiple component instances
const sharedMenu = ref<MenuItem[]>([]);
let menuLoadPromise: Promise<void> | null = null;

// Exposed for testing only
export const _resetMenuCache = () => {
  sharedMenu.value = [];
  menuLoadPromise = null;
};

const loadMenuOnce = (): Promise<void> => {
  if (menuLoadPromise !== null) {
    return menuLoadPromise;
  }

  menuLoadPromise = getAccountMenu()
    .then((rawItems) => {
      sharedMenu.value = rawItems.map(flattenMetadata);
    })
    .catch(() => {
      menuLoadPromise = null;
    });

  return menuLoadPromise;
};

export const useAccountMenu = (withExternalLink: boolean, surface = 'sidebar'): UseAccountMenuReturn => {
  const router = useRouter();
  const route = useRoute();

  onMounted(() => {
    loadMenuOnce();
  });

  const isExternal = (route: string): boolean => {
    return sharedMenu.value.some((item) => item.route === route && item.external);
  };

  const navigate = (route: string) => {
    if (isExternal(route)) {
      window.open(route, '_blank');
      return;
    }

    if (route === '/account/smart-reorder') {
      router.push({ path: '/account/smart-reorder', query: { source: 'sidebar' } });
      return;
    }

    if (withExternalLink || route.includes('v2')) {
      window.location.href = route;
    } else {
      router.push(route);
    }
  };

  const getCurrentUrl = computed(() => {
    return route.path;
  });

  const items = computed(() => sharedMenu.value.filter((item) => !item.surfaces || item.surfaces.includes(surface)));

  return {
    items,
    navigate,
    getCurrentUrl,
  };
};
